import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import Button from '../../components/ui/Button';
import { getSsoTestResult } from '../../services/ssoService';

// Bağlantı testi sonucu (spec §10.1) — #c=<bağlantı>&id=<test>. Oturum AÇILMAZ; yalnız testi başlatan yönetici görür
// (sunucu denetler). IdP'den gelen claim'ler, seçilen eşleştirme değeri ve eşleşen kullanıcı gösterilir.
const Page = styled.div`display: flex; flex-direction: column; gap: 16px; padding: 24px; max-width: 960px;`;
const Card = styled.div`
  background: #fff; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0;
  display: flex; flex-direction: column; gap: 10px;
`;
const Row = styled.div`display: flex; gap: 8px; align-items: center;`;
const Mono = styled.code`font-family: monospace; font-size: 12px; word-break: break-all;`;
const ClaimTable = styled.table`
  width: 100%; border-collapse: collapse; font-size: 12px;
  td { border-top: 1px solid #F0F2F5; padding: 6px 8px; vertical-align: top; }
  td:first-child { width: 40%; font-family: monospace; color: #475467; word-break: break-all; }
`;

export function readTestFragment(hash) {
  const p = new URLSearchParams(String(hash || '').replace(/^#/, ''));
  return { connectionId: p.get('c'), testId: p.get('id') };
}

export default function SsoTestResultPage() {
  const { t } = useTranslation();
  const [state, setState] = useState({ kind: 'loading' });
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    // Fragment adreste KALIR: sonuç tek kullanımlık değildir (10 dk, yalnız testi başlatan yönetici okur) — sayfa yenilenebilir.
    const { connectionId, testId } = readTestFragment(window.location.hash);
    if (!connectionId || !testId) { setState({ kind: 'missing' }); return; }
    getSsoTestResult(connectionId, testId).then((res) => {
      setState(res?.data?.status ? { kind: 'ready', result: res.data.data } : { kind: 'missing' });
    });
  }, []);

  if (state.kind === 'loading') return <Page><Card>{t('common.loading')}</Card></Page>;
  if (state.kind === 'missing') {
    return <Page><Card><Row><AlertTriangle size={18} />{t('responseErrors.sso.testResultNotFound')}</Row></Card></Page>;
  }

  const r = state.result;
  const claims = Object.entries(r.claims || {});
  return (
    <Page data-role="sso-test-result">
      <Card>
        <Row>
          {r.succeeded ? <CheckCircle2 size={20} color="#0F7B5A" /> : <AlertTriangle size={20} color="#B42318" />}
          <strong>{r.succeeded ? t('sso.test.succeeded') : t('sso.test.failed')}</strong>
        </Row>
        {!r.succeeded && r.errorKey && <div>{t(`responseErrors.${r.errorKey}`, { defaultValue: r.errorKey })}</div>}
        {r.detail && <Mono>{r.detail}</Mono>}
        {r.domainWarning && <div>{t('sso.test.domainWarning')}</div>}
        <div>{t('sso.test.matchValue')}: <Mono>{r.matchValue || '—'}</Mono></div>
        <div>{t('sso.test.matchedUser')}: <strong>{r.matchedUserName || '—'}</strong></div>
      </Card>
      <Card>
        <strong>{t('sso.test.claims')}</strong>
        {claims.length === 0 ? <div>—</div> : (
          <ClaimTable><tbody>
            {claims.map(([k, v]) => <tr key={k}><td>{k}</td><td>{(v || []).join(', ')}</td></tr>)}
          </tbody></ClaimTable>
        )}
      </Card>
      <Row>
        <Button type="button" onClick={() => window.close()}>{t('sso.test.close')}</Button>
      </Row>
    </Page>
  );
}
